import { motion } from "framer-motion";
import { Copy, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Stamp } from "@/components/fronk/Stamp";
import { STONKFUN_URL, copyMint } from "@/lib/fronk";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const STEPS = [
  {
    num: "STEP 001",
    title: "Get a desk",
    img: "/fronk/props/green-coin-sack.webp",
    text: "Buy $FRONK on StonkFun. The token trades against TOAD. Your desk opens the moment the coins land.",
  },
  {
    num: "STEP 002",
    title: "Keep it stocked",
    img: "/fronk/props/brass-rubber-stamp.webp",
    text: "Hold at least 20 dollars of FRONK. Desks under the minimum sit out the payout run. The stamp is unforgiving.",
  },
  {
    num: "STEP 003",
    title: "Watch the mail",
    img: "/fronk/props/shareholder-envelope.webp",
    text: "Distributions accumulate and go out in TOAD when a run makes sense. The mailroom handles the rest.",
  },
];

export const Onboarding = () => {
  return (
    <section
      id="payroll"
      className="paper-grain relative overflow-hidden bg-[color:var(--paper-1)] py-16 sm:py-20 lg:py-28"
      data-testid="onboarding-section"
    >
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp}>
          <Stamp>Onboarding packet</Stamp>
          <h2 className="font-heading mt-5 text-3xl font-bold leading-tight text-[color:var(--ink-900)] sm:text-4xl lg:text-5xl">
            YOUR DESK. YOUR SHARE.
          </h2>
          <p className="font-body mt-4 max-w-xl text-base leading-relaxed text-[color:var(--ink-800)]">
            Three forms. No waiting room. The whole process fits on one page
            because the mailroom insisted.
          </p>
        </motion.div>

        <div
          className="mt-12 grid gap-5 md:grid-cols-3"
          data-testid="onboarding-steps"
        >
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.5,
                delay: i * 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="ink-frame relative rounded-2xl bg-[color:var(--paper-0)] p-6"
              data-testid={`onboarding-step-${i + 1}`}
            >
              <span className="stamp-chip absolute -top-3 left-5">{s.num}</span>
              <div className="flex h-32 items-center justify-center">
                <img
                  src={s.img}
                  alt=""
                  className="h-28 object-contain drop-shadow-md"
                  loading="lazy"
                />
              </div>
              <h3 className="font-heading mt-4 text-2xl font-bold text-[color:var(--ink-900)]">
                {s.title}
              </h3>
              <p className="font-body mt-2 text-sm leading-relaxed text-[color:var(--ink-800)]">
                {s.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mt-14 flex flex-col items-center gap-6 sm:flex-row sm:justify-center"
        >
          <img
            src="/fronk/characters/fronk-pointing.webp"
            alt="FRONK pointing at the buttons"
            className="w-40 drop-shadow-lg sm:w-48"
            loading="lazy"
          />
          <div className="flex flex-col items-center gap-3 sm:items-start">
            <p className="font-mono-ledger text-[11px] uppercase tracking-[0.2em] text-[color:var(--ink-700)]">
              The chairman points at the buttons. This counts as guidance.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="rounded-xl bg-[color:var(--stamp-600)] px-6 font-mono-ledger text-sm uppercase tracking-[0.12em] text-[color:var(--paper-0)] shadow-[var(--paper-lift)] transition-colors hover:bg-[color:var(--stamp-500)] active:translate-y-[1px]"
                data-testid="onboarding-buy-button"
              >
                <a href={STONKFUN_URL} target="_blank" rel="noopener noreferrer">
                  Buy $FRONK on StonkFun
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={copyMint}
                className="rounded-xl border-[color:var(--ink-800)] bg-transparent px-6 font-mono-ledger text-sm uppercase tracking-[0.12em] text-[color:var(--ink-900)] transition-colors hover:bg-[color:var(--paper-2)]"
                data-testid="onboarding-copy-mint-button"
              >
                <Copy className="mr-1 h-4 w-4" />
                Copy mint
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
